import type { NovaEvent } from "@nova/types";
import { eventTypes, originator, type PersonSourceEvent } from "./types";

export const events = {
  /**
   * Fired when user click edit for a person source
   * @param sourceId
   * @returns
   */
  onEditPersonSourceCard: (sourceId: string): PersonSourceEvent => ({
    originator,
    type: eventTypes.onEditPersonSourceCard,
    data: () => sourceId,
  }),

  /**
   * Fired when user successfully unlinks a person source
   * @param successMessage
   * @param unlinkedPersonIds
   * @param canDismiss
   * @param directoryObjectId
   */
  onUnlinkPersonSourceSuccess: (
    successMessage: string,
    personIds: string[],
    canDismiss: boolean,
    directoryObjectId: string | null | undefined
  ): PersonSourceEvent => ({
    originator,
    type: eventTypes.onUnlinkPersonSourceSuccess,
    data: () => ({ successMessage, personIds, canDismiss, directoryObjectId }),
  }),

  /**
   * Fired when user fails to unlink a person source
   * @param errorMessage
   * @param failureCode
   */
  onUnlinkPersonSourceFailure: (
    errorMessage: string,
    failureCode: string
  ): PersonSourceEvent => ({
    originator,
    type: eventTypes.onUnlinkPersonSourceFailure,
    data: () => ({ errorMessage, failureCode }),
  }),

  /**
   * Fired when user successfully deletes a person source
   * @param successMessage
   * @param personId
   * @param canDismiss
   */
  onDeletePersonSourceSuccess: (
    successMessage: string,
    personId: string | null,
    canDismiss: boolean
  ): PersonSourceEvent => ({
    originator,
    type: eventTypes.onDeletePersonSourceSuccess,
    data: () => ({ successMessage, personId, canDismiss }),
  }),

  /**
   * Fired when user fails to delete a person source
   * @param errorMessage
   * @param failureCode
   */
  onDeletePersonSourceFailure: (
    errorMessage: string,
    failureCode: string
  ): PersonSourceEvent => ({
    originator,
    type: eventTypes.onDeletePersonSourceFailure,
    data: () => ({ errorMessage, failureCode }),
  }),

  /**
   * Fired when user clicks any phone on the person source card
   * @param phoneUrl : The phone number url of the person source.
   */
  onClickPhone: (phoneUrl: string): PersonSourceEvent => ({
    originator,
    type: eventTypes.onClickPhone,
    data: () => phoneUrl,
  }),

  /**
   * Fired when user clicks the email on the person source card
   * @param emailUrl
   * @param email
   */
  onClickEmail: (emailUrl: string, email: string): PersonSourceEvent => ({
    originator,
    type: eventTypes.onClickEmail,
    data: () => ({ emailUrl, email }),
  }),

  /**
   * Fired when user clicks the imAddress on the person source card
   * @param imAddressUrl - The instant messaging address url of the person source.
   */
  onClickImAddress: (imAddressUrl: string): PersonSourceEvent => ({
    originator,
    type: eventTypes.onClickIMAddress,
    data: () => imAddressUrl,
  }),

  onDismiss: (
    personID: string,
    showUpdatedPersonOnClose: boolean
  ): PersonSourceEvent => ({
    originator,
    type: eventTypes.onDismiss,
    data: () => ({ personID, showUpdatedPersonOnClose }),
  }),

  /**
   * Fired when a contact is saved successfully from Live Persona Editor.
   * @param contactId
   * @param successMessage
   */
  onSaveContactSuccess: (
    contactId: string,
    successMessage: string
  ): PersonSourceEvent => ({
    originator,
    type: eventTypes.onSaveContactSuccess,
    data: () => ({ contactId, successMessage }),
  }),

  /**
   * Fired when a contact fails to save from Live Persona Editor.
   * @param errorCode
   * @param errorMessage
   */
  onSaveContactFailure: (
    errorCode: string,
    errorMessage: string
  ): PersonSourceEvent => ({
    originator,
    type: eventTypes.onSaveContactFailure,
    data: () => ({ errorCode, errorMessage }),
  }),
} as const;

export const isPersonSourcesEvent = (
  event: NovaEvent<unknown>
): event is PersonSourceEvent => originator === event.originator;
